/** @file projectMethods: projects (games) — list, create, get, update, trash → purge, and export-all. */
import type { ProjectCreateBody, ProjectSummary, ProjectUpdateBody } from '../types/rest';
import type { ExportQuery, ProjectDetail, ProjectListEntry, ProjectListReply } from '../types/restAdded';
import type { ClientOptions } from './clientOptions';
import { fillPath } from './fillPath';
import { request } from './request';
import { toExportParams } from './toExportParams';

/** The `projects` member of the client: a project is a collection of puppets with shared defaults. */
export type ProjectMethods = {
  /** `GET /api/v1/projects`: live projects, or the trash with `trashed: true`. */
  list: (q?: { trashed?: boolean }) => Promise<ProjectListEntry[]>;
  /** `POST /api/v1/projects`. */
  create: (body: ProjectCreateBody) => Promise<ProjectSummary>;
  /** `GET /api/v1/projects/{id}`: the project and its live puppets. */
  get: (id: string) => Promise<ProjectDetail>;
  /** `PATCH /api/v1/projects/{id}`: rename and/or replace the defaults. */
  update: (id: string, body: ProjectUpdateBody) => Promise<ProjectSummary>;
  /** `DELETE /api/v1/projects/{id}`: moves the project and its live puppets to the trash. */
  trash: (id: string) => Promise<void>;
  /** `POST /api/v1/projects/{id}/restore`: back from the trash, with the puppets trashed with it. */
  restore: (id: string) => Promise<void>;
  /** `POST /api/v1/projects/{id}/purge`: deletes a trashed project and its puppets for good (`NOT_TRASHED` otherwise). */
  purge: (id: string) => Promise<void>;
  /** `GET /api/v1/projects/{id}/export`: one zip with `puppets/<id>/…` per live puppet (bundles, or baked sheets) and `project.json`. */
  export: (id: string, q?: ExportQuery) => Promise<Uint8Array>;
};

const PROJECT = '/api/v1/projects/{id}';

/** Project calls over `opts`; trash and purge are separate calls by design (never chained here). */
export function projectMethods(opts: ClientOptions): ProjectMethods {
  const empty = async (method: 'POST' | 'DELETE', template: string, id: string): Promise<void> => {
    await request(opts, { method, path: fillPath(template, { id }), accept: 'empty' });
  };
  return {
    list: async (q = {}) => {
      const query = { trashed: q.trashed === undefined ? undefined : String(q.trashed) };
      return (await request<ProjectListReply>(opts, { method: 'GET', path: '/api/v1/projects', query, accept: 'json' })).value.projects;
    },
    create: async (body) => (await request<ProjectSummary>(opts, { method: 'POST', path: '/api/v1/projects', body, accept: 'json' })).value,
    get: async (id) => (await request<ProjectDetail>(opts, { method: 'GET', path: fillPath(PROJECT, { id }), accept: 'json' })).value,
    update: async (id, body) => (await request<ProjectSummary>(opts, { method: 'PATCH', path: fillPath(PROJECT, { id }), body, accept: 'json' })).value,
    trash: (id) => empty('DELETE', PROJECT, id),
    restore: (id) => empty('POST', `${PROJECT}/restore`, id),
    purge: (id) => empty('POST', `${PROJECT}/purge`, id),
    export: async (id, q) => (await request<Uint8Array>(opts, {
      method: 'GET', path: fillPath(`${PROJECT}/export`, { id }), query: toExportParams(q), accept: 'bytes',
    })).value,
  };
}
